/**
 * EstadoAnmatIndicator — dot de estado del SNT/ANMAT en el header.
 *
 * Consume `useAnmatStatus()` (proxy del backend a riga-traza-v2) cada 10s.
 *
 * Colores del dot:
 *   - Amarillo titilando: hay una consulta en vuelo (isFetching).
 *   - Verde:              ok === true && latency_ms <= 10000.
 *   - Naranja:            ok === true && latency_ms > 10000 (lento).
 *   - Rojo:               ok === false (incluye errores de red al backend).
 *   - Gris:               nunca recibimos respuesta todavía.
 *
 * Tooltip muestra latency, GLN checkeado, razon_social o error.
 */

import { useNavigate } from 'react-router-dom';
import { useAnmatStatus } from '@/api/queries';
import type { AnmatStatus } from '@/api/contracts/anmat-status';
import { cn } from '@/lib/utils';

interface EstadoVisual {
  dotClass: string;
  textClass: string;
  label: string;
}

const LATENCIA_LENTA_MS = 10_000;

function deriveEstado(status: AnmatStatus | undefined): EstadoVisual {
  if (!status) {
    return { dotClass: 'bg-gray-400', textClass: 'text-gray-600', label: 'Sin datos' };
  }
  if (status.ok) {
    if ((status.latency_ms ?? 0) > LATENCIA_LENTA_MS) {
      return { dotClass: 'bg-orange-500', textClass: 'text-orange-700', label: 'Lento' };
    }
    return { dotClass: 'bg-green-500', textClass: 'text-green-700', label: 'Operativo' };
  }
  return { dotClass: 'bg-red-500', textClass: 'text-red-700', label: 'Con incidencias' };
}

function buildTooltip(status: AnmatStatus | undefined, isFetching: boolean): string {
  if (isFetching && !status) return 'Consultando estado ANMAT...';
  if (!status) return 'Estado SNT — sin datos';
  const partes: string[] = [];
  if (status.gln_checked) partes.push(`GLN ${status.gln_checked}`);
  if (typeof status.latency_ms === 'number') partes.push(`${status.latency_ms} ms`);
  if (status.razon_social) partes.push(status.razon_social);
  if (status.error) partes.push(`Error: ${status.error}`);
  const refresh = isFetching ? ' · consultando...' : '';
  return `Estado SNT — ${partes.join(' · ') || (status.ok ? 'OK' : 'sin detalle')}${refresh}`;
}

export function EstadoAnmatIndicator() {
  const navigate = useNavigate();
  const { data, isFetching } = useAnmatStatus();
  const visual = deriveEstado(data);

  const handleClick = () => void navigate('/anmat');

  return (
    <button
      type="button"
      onClick={handleClick}
      className="text-right text-xs transition hover:opacity-80"
      title={buildTooltip(data, isFetching)}
      aria-label={`Estado SNT: ${visual.label}`}
    >
      <div className="text-gray-600">Estado SNT:</div>
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'h-2 w-2 rounded-full transition-colors duration-150',
            // Mientras hay un request en vuelo titilamos en amarillo,
            // sobreescribiendo el color base — así se nota el polling.
            isFetching ? 'animate-pulse bg-yellow-400' : visual.dotClass,
          )}
        />
        <span
          className={cn(
            'font-semibold',
            isFetching && !data ? 'text-yellow-700' : visual.textClass,
          )}
        >
          {isFetching && !data ? 'Consultando...' : visual.label}
        </span>
      </div>
    </button>
  );
}
